import PreferencesRequest from "@/utils/constants/PreferencesRequest.ts";

const DB_NAME = 'NamaPreferencesCache';
const DB_VERSION = 1;
const STORE_NAME = 'preferences';
const CACHE_ID_KEY = '__cacheId__';

type CacheableProperty = typeof PreferencesRequest.META_DATA
  | typeof PreferencesRequest.LIST_LAYOUT
  | typeof PreferencesRequest.EDIT_LAYOUT
  | typeof PreferencesRequest.SEARCHER_LAYOUT;

const CACHEABLE_PROPERTIES: CacheableProperty[] = [
  PreferencesRequest.META_DATA,
  PreferencesRequest.LIST_LAYOUT,
  PreferencesRequest.EDIT_LAYOUT,
  PreferencesRequest.SEARCHER_LAYOUT
];

export default class PreferencesCache {
  private static db: IDBDatabase | null = null;
  private static initPromise: Promise<IDBDatabase> | null = null;

  private static async getDB(): Promise<IDBDatabase> {
    if (this.db) return this.db;
    if (this.initPromise) return this.initPromise;

    this.initPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        console.error('Failed to open PreferencesCache database:', request.error);
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        resolve(this.db);
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME);
        }
      };
    });

    return this.initPromise;
  }

  static async validateCacheId(serverCacheId: number | string | undefined): Promise<void> {
    if (serverCacheId === undefined || serverCacheId === null) return;

    try {
      const db = await this.getDB();
      const storedCacheId = await this.getFromStore(db, CACHE_ID_KEY);

      if (storedCacheId !== serverCacheId) {
        console.log(`Cache invalidated: stored=${storedCacheId}, server=${serverCacheId}`);
        await this.clearAll();
        await this.putInStore(db, CACHE_ID_KEY, serverCacheId);
      }
    } catch (e) {
      console.error('Error validating cache ID:', e);
    }
  }

  static async getCachedPreferences(
    ownerId: string,
    requestedProperties: string[],
    editLayoutScreenName?: string,
    listLayoutScreenName?: string,
    searcherLayoutScreenName?: string
  ): Promise<{ cached: Record<string, any>, uncachedProperties: string[] }> {
    const cached: Record<string, any> = {};
    const uncachedProperties: string[] = [];

    try {
      const db = await this.getDB();

      for (const property of requestedProperties) {
        if (!this.isCacheableProperty(property)) {
          uncachedProperties.push(property);
          continue;
        }

        const cacheKey = this.buildCacheKey(ownerId, property, editLayoutScreenName, listLayoutScreenName, searcherLayoutScreenName);
        const cachedValue = await this.getFromStore(db, cacheKey);

        if (cachedValue !== undefined) {
          cached[property] = cachedValue;
        } else {
          uncachedProperties.push(property);
        }
      }
    } catch (e) {
      console.error('Error reading from preferences cache:', e);
      return {cached: {}, uncachedProperties: requestedProperties};
    }

    return {cached, uncachedProperties};
  }

  static async cachePreferences(
    ownerId: string,
    preferences: Record<string, any>,
    editLayoutScreenName?: string,
    listLayoutScreenName?: string,
    searcherLayoutScreenName?: string
  ): Promise<void> {
    try {
      const db = await this.getDB();

      for (const property of CACHEABLE_PROPERTIES) {
        if (preferences[property] !== undefined) {
          const cacheKey = this.buildCacheKey(ownerId, property, editLayoutScreenName, listLayoutScreenName, searcherLayoutScreenName);
          await this.putInStore(db, cacheKey, preferences[property]);
        }
      }
    } catch (e) {
      console.error('Error writing to preferences cache:', e);
    }
  }

  private static buildCacheKey(
    ownerId: string,
    property: string,
    editLayoutScreenName?: string,
    listLayoutScreenName?: string,
    searcherLayoutScreenName?: string
  ): string {
    let key = `${ownerId}::${property}`;

    if (property === PreferencesRequest.EDIT_LAYOUT && editLayoutScreenName) {
      key += `::${editLayoutScreenName}`;
    } else if (property === PreferencesRequest.LIST_LAYOUT && listLayoutScreenName) {
      key += `::${listLayoutScreenName}`;
    } else if (property === PreferencesRequest.SEARCHER_LAYOUT && searcherLayoutScreenName) {
      key += `::${searcherLayoutScreenName}`;
    }

    return key;
  }

  private static isCacheableProperty(property: string): property is CacheableProperty {
    return CACHEABLE_PROPERTIES.includes(property as CacheableProperty);
  }

  static async clearAll(): Promise<void> {
    try {
      const db = await this.getDB();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction(STORE_NAME, 'readwrite');
        const store = transaction.objectStore(STORE_NAME);
        const request = store.clear();

        request.onsuccess = () => {
          console.log('Preferences cache cleared');
          resolve();
        };
        request.onerror = () => reject(request.error);
      });
    } catch (e) {
      console.error('Error clearing preferences cache:', e);
    }
  }

  private static getFromStore(db: IDBDatabase, key: string): Promise<any> {
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.get(key);

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  private static putInStore(db: IDBDatabase, key: string, value: any): Promise<void> {
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.put(value, key);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }
}
